import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { appUrl, headers, multipartHeaders, restApiUrl } from "./app_props";
import { useAuth } from "./AuthContext";
import RiskMatrixInfo from "./RiskMatrixInfo";
import IconButton from "../components/IconButton";
import { usePopup } from "../components/PopupProvider";
import { Button, Box, Drawer, TextField, Select, MenuItem, FormControl, InputLabel, Autocomplete } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faPlus } from "@fortawesome/free-solid-svg-icons";

const pdControlSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: "26px",
    borderRadius: "6px",
    backgroundColor: "var(--pd-surface)",
    fontSize: "12.5px",
    padding: "0 !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--pd-line-strong)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--pd-line-strong)",
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--pd-accent) !important",
    borderWidth: "1px !important",
  },
  "& input": { padding: "4px 8px !important", fontSize: "12.5px" },
  "& .MuiAutocomplete-input": { padding: "1px 4px !important" },
  "& .MuiSelect-select": { padding: "4px 8px !important", minHeight: "0 !important", fontSize: "12.5px", display: "flex", alignItems: "center" },
  "& textarea": { fontSize: "12.5px", padding: "4px 8px !important" },
  "& legend": { display: "none" },
  "& .MuiInputLabel-root": { display: "none" },
};

// Label above a control, matching the pd-field-label style used on Permit Detail.
const FieldWrap = ({ label, required, wide, children }) => (
  <div className={wide ? "pd-field wide" : "pd-field"} style={{ minWidth: 0 }}>
    <label className="pd-field-label" style={{ display: "block", marginBottom: "3px" }}>
      {label}
      {required && <span style={{ color: "var(--pd-crit)", marginLeft: "2px" }}>*</span>}
    </label>
    {children}
  </div>
);

const Field = ({ label, value, className = "" }) => (
  <div className={`pd-field ${className}`}>
    <div className="pd-field-label">{label}</div>
    <div className="pd-field-value">{value}</div>
  </div>
);

const PermitWizard = () => {
  const { showConfirm, showSuccess, showError, showWarning, showInfo } =
    usePopup();
  const header = useMemo(() => headers(), []);
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
  const { user } = useAuth();
  const [addFullWidth, setAddFullWidth] = useState(false);
  const [permitStatus, setPermitStatus] = useState(null);

  // API base for direct links (anchors won't be proxied by CRA dev server)
  const API_BASE = appUrl;

  // per-permit-type checklists are stored inside each permit type block

  const [attachments, setAttachments] = useState([]); // array of files
  const [existingAttachments, setExistingAttachments] = useState([]); // attachments from server when editing

  const [permitTypes, setPermitTypes] = useState([]);
  const [permitTypeChecklistMap, setPermitTypeChecklistMap] = useState({});
  const [allChecklists, setAllChecklists] = useState([]);
  const [locations, setLocations] = useState([]);
  const [hazards, setHazards] = useState([]);
  const [controlMeasures, setControlMeasures] = useState([]);
  const getEffectiveControlIds = (hazardItem) => {
    const existing = Array.isArray(hazardItem.controlIds) ? hazardItem.controlIds.map(String) : [];
    const mandatoryIds = controlMeasures
      .filter((cm) => String(cm.hazardId) === String(hazardItem.hazardId) && cm.mandatory)
      .map((cm) => String(cm.id));
    return Array.from(new Set([...existing, ...mandatoryIds]));
  };
  const [riskMatrices, setRiskMatrices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [hazardsPage, setHazardsPage] = useState(0);
  const [hazardsLoading, setHazardsLoading] = useState(false);
  const [hazardsHasMore, setHazardsHasMore] = useState(true);

  const [controlsPage, setControlsPage] = useState(0);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [controlsHasMore, setControlsHasMore] = useState(true);

  const [riskMatricesPage, setRiskMatricesPage] = useState(0);
  const [riskMatricesLoading, setRiskMatricesLoading] = useState(false);
  const [riskMatricesHasMore, setRiskMatricesHasMore] = useState(true);
  // Page size for paginated master lists (incremental load)
  const PAGE_SIZE = 10;
  const [openHazardIndex, setOpenHazardIndex] = useState(null);
  const [employees, setEmployees] = useState([]);
  // Parse sites from token
  const rawSites = userInfo.sites || [];
  let sitesList = [];
  if (typeof rawSites === "string") {
    try {
      const parsed = JSON.parse(rawSites);
      if (Array.isArray(parsed)) {
        sitesList = parsed.map((s) =>
          typeof s === "string" ? { id: null, name: s } : s,
        );
      } else if (parsed) {
        sitesList = [parsed];
      }
    } catch (e) {
      // fallback: comma-separated values
      sitesList = rawSites.split(",").map((s) => {
        const trimmed = s.trim();
        const parts = trimmed.split(":");
        if (parts.length === 2 && !isNaN(parts[0])) {
          return { id: parseInt(parts[0], 10), name: parts[1].trim() };
        }
        if (!isNaN(trimmed))
          return { id: parseInt(trimmed, 10), name: String(trimmed) };
        return { id: null, name: trimmed };
      });
    }
  } else if (Array.isArray(rawSites)) {
    sitesList = rawSites.map((s) =>
      typeof s === "string" ? { id: null, name: s } : s,
    );
  }

  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  // Site and Department states
  const [selectedSite, setSelectedSite] = useState(
    sitesList.length > 0 ? sitesList[0] : null,
  );
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedIssuerDepartment, setSelectedIssuerDepartment] = useState("");
  const [formData, setFormData] = useState({
    jobDescription: "",
    permitAutoRejectOnScheduleTime: false,
    revalidationRequired: false,
    revalidationIntervalHours: "",
    permitScope: "STANDARD",
    // keep legacy single type for compatibility but prefer permitTypeBlocks
    typeId: "",
    locationId: "",
    equipmentId: "",
    scheduleStart: "",
    scheduleEnd: "",
    contractorName: "",
    externalWorkOrderId: "",
    residualRiskLevel: "",
    siteId: selectedSite?.id || "",
    receiverDepartmentId: "",
    issuerDepartmentId: "",
    priority: "MEDIUM",
  });

  const [riskAssessmentData, setRiskAssessmentData] = useState({
    hazards: [], // array of {hazardId, controlIds: [], taskDescription: "", responsiblePersonId: "", responsiblePersonName: ""}
    overallRisk: "",
  });

  const [permitTypeBlocksState, setPermitTypeBlocksState] = useState([]);
  const [approvedContractors, setApprovedContractors] = useState([]);
  const [addingPermitType, setAddingPermitType] = useState(false);
  const [editingPermitIndex, setEditingPermitIndex] = useState(null);
  const [selectedClearanceDepartment, setSelectedClearanceDepartment] =
    useState("");
  // clearance departments are stored at permit level (not per permit-type)
  const [permitClearanceDepartments, setPermitClearanceDepartments] = useState(
    [],
  );
  const [newPermitTypeForm, setNewPermitTypeForm] = useState({
    permitTypeId: "",
    residualRiskLevel: "",
    contractorName: "",
    externalWorkOrderId: "",
    equipmentId: "",
    reviewerRemark: "",
    approved: false,
    checklistItems: [],
  });

  // Mentioned default departments to pre-select when available on a site
  const MENTIONED_DEPARTMENTS = [
    "Process",
    "Electrical",
    "Mechanical",
    "Civil",
    "Instrument",
    "R&D/QCD",
    "Fire",
    "Store",
  ];

  const normalizeName = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const areClearancesApproved = () => {
    try {
      const cds = permitClearanceDepartments || [];
      if (!cds || cds.length === 0) return true;
      return cds.every((cd) => {
        if (!cd) return true;
        if (cd.approved) return true;
        if (cd.approvedBy || cd.approvedById) return true;
        return false;
      });
    } catch (e) {
      return true;
    }
  };

  const buildMentionedDefaults = (deptList = []) => {
    const normMentioned = new Set(
      MENTIONED_DEPARTMENTS.map((n) => normalizeName(n)),
    );
    const seen = new Set();
    const defaults = [];
    (deptList || []).forEach((d) => {
      if (!d) return;
      const nn = normalizeName(d.name);
      if (normMentioned.has(nn) && !seen.has(nn)) {
        seen.add(nn);
        defaults.push({
          id: null,
          departmentId: String(d.id),
          departmentName: d.name,
          approved: false,
        });
      }
    });
    return defaults;
  };

  // Load functions with useCallback to avoid dependency warnings
  const loadHazards = useCallback(
    async (page, append) => {
      setHazardsLoading(true);
      try {
        const res = await fetch(
          `${restApiUrl}/api/hazards?page=${page}&size=${PAGE_SIZE}&sort=name,asc`,
          { method: "GET", mode: "cors", headers: header },
        );
        const json = await res.json();
        const items =
          (json && json.data && json.data.content) || json.data || [];
        setHazards((prev) => (append ? [...prev, ...items] : items));
        setHazardsPage(page);
        setHazardsHasMore(items && items.length === PAGE_SIZE);
      } catch (err) {
        console.error("Failed to load hazards", err);
      } finally {
        setHazardsLoading(false);
      }
    },
    [header, PAGE_SIZE],
  );

  const loadControlMeasures = useCallback(
    async (page, append) => {
      setControlsLoading(true);
      try {
        // Fetch all control measures in one go so the UI shows the full list
        const res = await fetch(
          `${restApiUrl}/api/controls?page=0&size=2000&sort=name,asc`,
          { method: "GET", mode: "cors", headers: header },
        );
        const json = await res.json();
        const items =
          (json && json.data && json.data.content) || json.data || [];
        // replace the list with the full set and disable incremental loading
        setControlMeasures(items || []);
        setControlsPage(0);
        setControlsHasMore(false);
      } catch (err) {
        console.error("Failed to load control measures", err);
      } finally {
        setControlsLoading(false);
      }
    },
    [header, PAGE_SIZE],
  );

  const loadRiskMatrices = useCallback(
    async (page, append) => {
      setRiskMatricesLoading(true);
      try {
        const res = await fetch(
          `${restApiUrl}/api/risk-matrix?page=${page}&size=${PAGE_SIZE}&sort=id,asc`,
          { method: "GET", mode: "cors", headers: header },
        );
        const json = await res.json();
        const items =
          (json && json.data && json.data.content) || json.data || [];
        setRiskMatrices((prev) => (append ? [...prev, ...items] : items));
        setRiskMatricesPage(page);
        setRiskMatricesHasMore(items && items.length === PAGE_SIZE);
      } catch (err) {
        console.error("Failed to load risk matrices", err);
      } finally {
        setRiskMatricesLoading(false);
      }
    },
    [PAGE_SIZE],
  );

  useEffect(() => {
    fetch(`${restApiUrl}/employees`, { method: "GET", mode: "cors", headers: header })
      .then((r) => r.json())
      .then((data) => setEmployees(Array.isArray(data) ? data : []))
      .catch(() => { });
  }, [header]);

  // Fetch master permit-type details (used when permit type objects don't include checklists)
  const fetchPermitTypeMasterChecklists = async (ptid) => {
    if (!ptid) return [];
    // prefer checklists specifically mapped to the permit type
    if (permitTypeChecklistMap && permitTypeChecklistMap[ptid]) return permitTypeChecklistMap[ptid];
    // fallback: if allChecklists loaded and permitType has checklistIds, filter them
    const pt = (permitTypes || []).find((t) => String(t.id) === String(ptid));
    if (pt && pt.checklistIds && Array.isArray(pt.checklistIds) && allChecklists && allChecklists.length > 0) {
      return allChecklists.filter((c) => pt.checklistIds.some((id) => String(id) === String(c.id)));
    }
    // last resort: return empty list
    return [];
  };

  // Build a map from permitTypeId -> checklist objects for quick lookup
  useEffect(() => {
    if (!(permitTypes && permitTypes.length > 0) || !(allChecklists && allChecklists.length > 0)) {
      setPermitTypeChecklistMap({});
      return;
    }
    const map = {};
    permitTypes.forEach((pt) => {
      if (pt && pt.checklistIds && Array.isArray(pt.checklistIds)) {
        map[String(pt.id)] = allChecklists.filter((c) => pt.checklistIds.some((id) => String(id) === String(c.id)));
      } else {
        map[String(pt.id)] = [];
      }
    });
    setPermitTypeChecklistMap(map);
  }, [permitTypes, allChecklists]);
  const [openRiskBefore, setOpenRiskBefore] = useState(false);

  const formatRiskById = (id) => {
    if (!id) return "";
    const rm = riskMatrices.find((r) => String(r.id) === String(id));
    if (!rm) return "";
    return `Likelihood: ${rm.likelihood}, Severity: ${rm.severity} (${rm.riskLevel})`;
  };

  // Ensure control measure details are available when viewing the Review step
  useEffect(() => {
    if (currentStep !== 4) return;
    // collect control ids used in the current risk assessment
    const needed = new Set();
    (riskAssessmentData.hazards || []).forEach((h) => {
      (h.controlIds || []).forEach((cid) => {
        if (!controlMeasures.some((cm) => String(cm.id) === String(cid))) {
          needed.add(cid);
        }
      });
    });
    if (needed.size === 0) return;

    const fetchMissing = async () => {
      const fetched = [];
      for (const cid of Array.from(needed)) {
        try {
          const res = await fetch(`${restApiUrl}/api/controls/${cid}`, {
            method: "GET",
            mode: "cors",
            headers: header,
          });
          if (!res.ok) continue;
          const json = await res.json();
          const item = (json && json.data) || json;
          if (item) fetched.push(item);
        } catch (err) {
          console.error("Failed to load control", cid, err);
        }
      }
      if (fetched.length > 0) {
        // append fetched controls to existing list (avoid duplicates)
        setControlMeasures((prev) => {
          const existingIds = new Set(prev.map((p) => String(p.id)));
          const toAdd = fetched.filter((f) => !existingIds.has(String(f.id)));
          return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });
      }
    };

    fetchMissing();
  }, [currentStep, riskAssessmentData, controlMeasures]);



  // refs for step indicator auto-scroll
  const stepContainerRef = useRef(null);
  const stepRefs = useRef([]);
  // suppress clearing/auto-add while loading server data
  const suppressSiteChangeRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isEditing) return;
    const loadFull = async () => {
      // prevent the site-change effect from clearing server-provided clearances
      suppressSiteChangeRef.current = true;
      try {
        const res = await fetch(`${restApiUrl}/api/permits/${id}/full`, {
          method: "GET",
          mode: "cors",
          headers: header,
        });
        const json = await res.json();
        if (!json.success) {
          console.error("Failed to load full permit", json);
          return;
        }
        const data = json.data;
        if (!data) return;

        // Permit
        const p = data.permit;
        const toInput = (dt) => {
          if (!dt) return "";
          // expect 'YYYY-MM-DDTHH:mm:ss' or ISO, trim seconds for datetime-local
          return dt.length >= 16 ? dt.substring(0, 16) : dt;
        };
        setFormData({
          typeId: p.typeId != null ? String(p.typeId) : "",
          locationId: p.locationId != null ? String(p.locationId) : "",
          equipmentId: p.equipmentId != null ? String(p.equipmentId) : "",
          scheduleStart: toInput(p.scheduleStart || ""),
          scheduleEnd: toInput(p.scheduleEnd || ""),
          contractorName: p.contractorName || "",
          jobDescription: p.jobDescription || "",
          permitAutoRejectOnScheduleTime:
            p.permitAutoRejectOnScheduleTime || false,
          revalidationRequired: p.revalidationRequired || false,
          revalidationIntervalHours: p.revalidationIntervalHours || "",
          permitScope: p.permitScope || "STANDARD",
          siteId: p.siteId != null ? String(p.siteId) : "",
          receiverDepartmentId:
            p.receiverDepartmentId != null
              ? String(p.receiverDepartmentId)
              : "",
          issuerDepartmentId:
            p.issuerDepartmentId != null ? String(p.issuerDepartmentId) : "",
          externalWorkOrderId: p.externalWorkOrderId || "",
          residualRiskLevel: p.residualRiskLevel || "",
          priority: p.priority || "MEDIUM",
        });

        // Ensure site and department selection reflect the loaded permit (for edit)
        let foundDeptList = [];
        if (p.siteId != null) {
          const foundSite = sitesList.find(
            (s) => String(s.id) === String(p.siteId) || s.id === p.siteId,
          );
          if (foundSite) {
            setSelectedSite(foundSite);
            // populate departments if site contains them; no fallback defaults
            if (
              foundSite.departmentList &&
              Array.isArray(foundSite.departmentList)
            ) {
              foundDeptList = foundSite.departmentList;
            } else if (
              foundSite.departments &&
              Array.isArray(foundSite.departments)
            ) {
              foundDeptList = foundSite.departments;
            } else {
              foundDeptList = [];
            }
            setDepartments(foundDeptList);
          }
        }
        if (p.receiverDepartmentId != null) {
          setSelectedDepartment(String(p.receiverDepartmentId));
        }
        if (p.issuerDepartmentId != null) {
          setSelectedIssuerDepartment(String(p.issuerDepartmentId));
        }

        setPermitStatus(p.status || null);

        // Load permit-level clearance departments into state
        setPermitClearanceDepartments(p.clearanceDepartments || []);

        // Risk Assessment
        const ra = data.riskAssessment;
        // Populate risk assessment state when editing so Overall Risk and
        // hazard/control selections are visible in the UI during edit.
        if (ra) {
          const items =
            // support both shapes: items may be on the root `data` or inside `riskAssessment`
            data.riskAssessmentItems || ra.riskAssessmentItems || ra.items || ra.riskItems || [];

          const normalizeHazardId = (it) => {
            if (it == null) return "";
            if (it.hazardId != null) return String(it.hazardId);
            if (it.hazard && it.hazard.id != null) return String(it.hazard.id);
            if (it.hazard && it.hazard.hazardId != null)
              return String(it.hazard.hazardId);
            // fallback: sometimes backend returns {hazard: <name>} or similar
            return "";
          };

          const normalizeControlIds = (it) => {
            if (!it) return [];
            // controlIds may be array of ids or array of objects
            const src = it.controlIds || it.controls || [];
            if (!Array.isArray(src)) return [];
            return src
              .map((c) => {
                if (c == null) return null;
                if (typeof c === "object") return String(c.id ?? c.controlId ?? c);
                return String(c);
              })
              .filter((v) => v != null && v !== "");
          };

          setRiskAssessmentData({
            hazards: Array.isArray(items)
              ? items.map((it) => ({
                hazardId: normalizeHazardId(it),
                controlIds: normalizeControlIds(it),
                otherControlText: it.otherControlText != null && it.otherControlText !== "" ? it.otherControlText : undefined,
                taskDescription: it.taskDescription || "",
                responsiblePersonId: it.responsiblePersonId ? String(it.responsiblePersonId) : "",
                responsiblePersonName: it.responsiblePersonName || "",
              }))
              : [],
            overallRisk:
              ra.overallRiskId != null
                ? String(ra.overallRiskId)
                : ra.overallRisk != null
                  ? String(ra.overallRisk)
                  : "",
          });
        }

        // Populate permit type blocks from server response. If checklistItems
        // are provided separately, attach them to their matching blocks.
        const ptBlocks = data.permitTypeBlocks || [];
        if (data.checklistItems && Array.isArray(data.checklistItems)) {
          const checklistMap = {};
          data.checklistItems.forEach((ci) => {
            const pid =
              ci.permitPermitTypeId ||
              (ci.permitPermitType && ci.permitPermitType.id) ||
              null;
            if (pid != null) {
              const key = String(pid);
              checklistMap[key] = checklistMap[key] || [];
              // attach master checklist object when available
              const enhanced = {
                ...ci,
                checklist:
                  (allChecklists || []).find(
                    (c) => String(c.id) === String(ci.permitTypeChecklistId),
                  ) || null,
              };
              checklistMap[key].push(enhanced);
            }
          });
          const blocksWithChecklist = ptBlocks.map((b) => {
            const byBlockId = checklistMap[String(b.id)];
            const byPermitTypeId = checklistMap[String(b.permitTypeId)];
            const sourceList =
              byBlockId || byPermitTypeId || b.checklistItems || [];
            return {
              ...b,
              checklistItems: sourceList.map((ci) =>
                typeof ci === "object"
                  ? {
                    ...ci,
                    checklist:
                      ci.checklist ||
                      (allChecklists || []).find(
                        (c) =>
                          String(c.id) === String(ci.permitTypeChecklistId),
                      ) ||
                      null,
                  }
                  : ci,
              ),
            };
          });
          setPermitTypeBlocksState(blocksWithChecklist);
        } else {
          setPermitTypeBlocksState(ptBlocks);
        }
      } catch (err) {
        console.error("Failed to load full permit", err);
      } finally {
        // allow site-change effect to run normally after load completes
        suppressSiteChangeRef.current = false;
      }
    };
    loadFull();
  }, [id, isEditing]);
  // No fallback/default selection logic is applied.

  // Load departments when site changes
  useEffect(() => {
    if (selectedSite) {
      let deptList = [];
      if (
        selectedSite.departmentList &&
        Array.isArray(selectedSite.departmentList)
      ) {
        deptList = selectedSite.departmentList;
      } else if (
        selectedSite.departments &&
        Array.isArray(selectedSite.departments)
      ) {
        deptList = selectedSite.departments;
      } else {
        // If site doesn't provide departments, treat as empty list.
        deptList = [];
      }

      setDepartments(deptList);

      const availableIds = new Set((deptList || []).map((d) => String(d.id)));
      const availableNames = new Set(
        (deptList || []).map((d) => String(d.name)),
      );

      // Ensure permit-level clearance departments only include departments available on the selected site.
      const filteredClearances = (permitClearanceDepartments || []).filter(
        (c) => {
          if (!c) return false;
          if (c.departmentId && availableIds.has(String(c.departmentId)))
            return true;
          if (c.departmentName && availableNames.has(String(c.departmentName)))
            return true;
          return false;
        },
      );
      if (
        (filteredClearances || []).length === 0 &&
        (deptList || []).length > 0
      ) {
        // If none remain, try to auto-populate with mentioned defaults
        const mentioned = buildMentionedDefaults(deptList);
        const chosen = selectedDepartment
          ? deptList.find(
            (d) =>
              String(d.id) === String(selectedDepartment) ||
              String(d.name) === String(selectedDepartment),
          )
          : null;
        const merged = [];
        const seen = new Set();
        if (chosen) {
          const key = String(chosen.id) + "|" + normalizeName(chosen.name);
          seen.add(key);
          merged.push({
            id: null,
            departmentId: String(chosen.id),
            departmentName: chosen.name,
            approved: false,
          });
        }
        mentioned.forEach((m) => {
          const key =
            String(m.departmentId) + "|" + normalizeName(m.departmentName);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(m);
          }
        });
        setPermitClearanceDepartments(merged);
      } else {
        setPermitClearanceDepartments(filteredClearances);
      }

      // Ensure permit-type block clearance departments only include
      // departments available on the selected site. If no departments
      // are available, clear the clearance lists.

      // No per-block clearance lists anymore; leave blocks unchanged
      setPermitTypeBlocksState((prev) => (prev || []).map((b) => ({ ...b })));
    } else {
      setDepartments([]);
    }
  }, [selectedSite]);

  // Load locations for a given site (used on initial load and when site changes)
  const loadLocations = async (site) => {
    try {
      let locUrl = `${restApiUrl}/api/locations?page=0&size=200&sort=name,asc`;
      if (site) {
        const siteFilter = site.id
          ? `siteId=${site.id}`
          : `siteName=${encodeURIComponent(site.name)}`;
        locUrl += `&${siteFilter}`;
      }

      const locRes = await fetch(locUrl, {
        method: "GET",
        mode: "cors",
        headers: header,
      });
      const locJson = await locRes.json();
      const locItems =
        (locJson && locJson.data && locJson.data.content) || locJson.data || [];
      setLocations(locItems || []);
    } catch (err) {
      console.error("Failed to load locations", err);
    }
  };

  // Load master data and initial lists (only run once on mount)
  useEffect(() => {
    const loadMasters = async () => {
      try {
        const ptRes = await fetch(
          `${restApiUrl}/api/permit-types?page=0&size=200&sort=name,asc`,
          {
            method: "GET",
            mode: "cors",
            headers: header,
          },
        );
        const ptJson = await ptRes.json();
        const ptItems =
          (ptJson && ptJson.data && ptJson.data.content) || ptJson.data || [];
        setPermitTypes(ptItems || []);

        // Fetch all master checklists once (checklist controller)
        try {
          const ckRes = await fetch(
            `${restApiUrl}/api/checklists?page=0&size=200&sort=name,asc`,
            { method: "GET", mode: "cors", headers: header },
          );
          const ckJson = await ckRes.json();
          const ckItems =
            (ckJson && ckJson.data && ckJson.data.content) || ckJson.data || [];
          setAllChecklists(ckItems || []);
        } catch (err) {
          console.error("Failed to load master checklists", err);
        }
      } catch (err) {
        console.error("Failed to load master lists", err);
      }

      // load initial lists. Fetch all risk matrices at once (no pagination)
      loadHazards(0, false);
      loadControlMeasures(0, false);
      try {
        const rmRes = await fetch(
          `${restApiUrl}/api/risk-matrix?page=0&size=2000&sort=id,asc`,
          { method: "GET", mode: "cors", headers: header },
        );
        const rmJson = await rmRes.json();
        const rmItems =
          (rmJson && rmJson.data && rmJson.data.content) || rmJson.data || [];
        setRiskMatrices(rmItems || []);
        setRiskMatricesPage(0);
        setRiskMatricesHasMore(false);
      } catch (err) {
        // fallback to paginated load if fetch fails
        console.error("Failed to load all risk matrices, falling back", err);
        loadRiskMatrices(0, false);
      }

      // Load locations for the initial site selection (if any)
      loadLocations(selectedSite);

      // Load approved contractors for the permit-type form
      fetch(`${restApiUrl}/api/contractors/approved`, { method: "GET", mode: "cors", headers: header })
        .then((r) => r.json())
        .then((j) => setApprovedContractors(Array.isArray(j) ? j : []))
        .catch(() => { });
    };
    loadMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadHazards, loadControlMeasures, loadRiskMatrices]);

  // Only reload locations when the selected site changes. Avoid reloading
  // other master lists when user switches site selection.
  useEffect(() => {
    loadLocations(selectedSite);
  }, [selectedSite]);

  // When master checklists load after a permit was fetched for edit,
  // enrich permitTypeBlocksState.checklistItems with the full checklist objects
  // so the checklist name/fields are visible when editing saved permits.
  useEffect(() => {
    if (!allChecklists || allChecklists.length === 0) return;
    setPermitTypeBlocksState((prev) => {
      if (!prev || prev.length === 0) return prev;
      let changed = false;
      const updated = prev.map((b) => {
        // if checklistItems missing or null, only populate from master checklists
        // when there is an explicit mapping for this permit type.
        if (!Array.isArray(b.checklistItems) || b.checklistItems.length === 0) {
          const ptId = b.permitTypeId || (b.permitType && b.permitType.id) || null;
          const mapped = ptId ? (permitTypeChecklistMap[String(ptId)] || []) : [];
          if (Array.isArray(mapped) && mapped.length > 0) {
            const items = mapped.map((mc) => ({
              text: mc.checklist || mc.name || mc.text || "",
              permitTypeChecklistId: mc.id,
              mandatory: false,
              checked: false,
              status: "PENDING",
              fields: mc.fields || [],
              fieldsValues: {},
            }));
            changed = true;
            return { ...b, checklistItems: items };
          }
          // no mapping found for this permit type: leave checklistItems empty
          return b;
        }

        const items = b.checklistItems.map((ci) => {
          if (
            ci &&
            !ci.checklist &&
            (ci.permitTypeChecklistId || ci.permitTypeChecklistId === 0)
          ) {
            const found = allChecklists.find(
              (c) => String(c.id) === String(ci.permitTypeChecklistId),
            );
            if (found) {
              changed = true;
              return {
                ...ci,
                checklist: found,
                text: ci.text || found.name || found.checklist || found.text,
                fields:
                  ci.fields && ci.fields.length > 0
                    ? ci.fields
                    : found.fields || [],
              };
            }
          }
          return ci;
        });
        return { ...b, checklistItems: items };
      });
      return changed ? updated : prev;
    });
  }, [allChecklists, permitTypeBlocksState, permitTypeChecklistMap]);

  // Auto-scroll step indicator so the active step is brought into view
  useEffect(() => {
    const el = stepRefs.current[currentStep - 1];
    if (
      el &&
      stepContainerRef.current &&
      typeof el.scrollIntoView === "function"
    ) {
      // Use inline 'center' to bring it into view on small screens
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [currentStep]);

  // Checklist is handled per-permit-type block now.

  const handleNext = () => {
    // step-level validation before advancing
    const validation = validateStep(currentStep);
    if (!validation.isValid) {
      showWarning(
        `Please fill required field(s): ${validation.missingFields.join(", ")}`,
      );
      return;
    }

    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const handleHazardsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollTop + clientHeight >= scrollHeight - 10 &&
      hazardsHasMore &&
      !hazardsLoading
    ) {
      loadHazards(hazardsPage + 1, true);
    }
  };

  const handleControlsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollTop + clientHeight >= scrollHeight - 10 &&
      controlsHasMore &&
      !controlsLoading
    ) {
      loadControlMeasures(controlsPage + 1, true);
    }
  };

  const handleRiskMatricesScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollTop + clientHeight >= scrollHeight - 10 &&
      riskMatricesHasMore &&
      !riskMatricesLoading
    ) {
      loadRiskMatrices(riskMatricesPage + 1, true);
    }
  };

  // Validate required fields for a given step. Returns object with isValid and missingFields array
  const validateStep = (step) => {
    const missingFields = [];

    if (step === 1) {
      if (!selectedSite) missingFields.push("Site");
      if (!selectedDepartment) missingFields.push("Receiver Department");
      if (!selectedIssuerDepartment) missingFields.push("Issuer Department");
      if (!formData.jobDescription) missingFields.push("Job Description");
      if (!permitTypeBlocksState || permitTypeBlocksState.length === 0)
        missingFields.push("Permit Types");
      if (!formData.locationId) missingFields.push("Location");
      if (!formData.scheduleStart) missingFields.push("Schedule Start");
      if (!formData.scheduleEnd) missingFields.push("Schedule End");

      return { isValid: missingFields.length === 0, missingFields };
    }
    if (step === 2) {
      if (!riskAssessmentData.overallRisk) missingFields.push("Overall Risk");
      (riskAssessmentData.hazards || []).forEach((h, idx) => {
        if (!h.responsiblePersonId) {
          missingFields.push(`Responsible Person (Step ${idx + 1})`);
        }
      });
      return { isValid: missingFields.length === 0, missingFields };
    }
    if (step === 3) {
      return { isValid: true, missingFields: [] };
    }
    // steps 4-5 have no required fields
    return { isValid: true, missingFields: [] };
  };

  // Validate all required fields across the wizard before final submit
  const validateAll = () => {
    // check steps 1-2
    const step1Result = validateStep(1);
    if (!step1Result.isValid) return step1Result;

    const step2Result = validateStep(2);
    if (!step2Result.isValid) return step2Result;

    // checklist validation: if a checklist item is checked (YES) and it has fields,
    // ensure all field values are provided (non-empty)
    const missing = [];
    (permitTypeBlocksState || []).forEach((b) => {
      (b.checklistItems || []).forEach((ci) => {
        const checkedFlag = ci.checked === true || ci.checked === "YES";
        if (checkedFlag && Array.isArray(ci.fields) && ci.fields.length > 0) {
          (ci.fields || []).forEach((f) => {
            const val = (ci.fieldsValues || {})[f.fieldName];
            if (
              val === undefined ||
              val === null ||
              String(val).trim() === ""
            ) {
              missing.push(`${ci.text} - ${f.label}`);
            }
          });
        }
      });
    });
    if (missing.length > 0) return { isValid: false, missingFields: missing };

    return { isValid: true, missingFields: [] };
  };

  const buildLocationHierarchy = (location) => {
    if (!location) return "";
    const hierarchy = [location.name];
    let current = location;
    const visited = new Set([location.id]); // Prevent infinite loops

    // Handle both parentId and parentName
    while ((current.parentId || current.parentName) && visited.size < 10) {
      let parent = null;

      if (current.parentId) {
        parent = locations.find(
          (l) => String(l.id) === String(current.parentId),
        );
      } else if (current.parentName) {
        parent = locations.find((l) => l.name === current.parentName);
      }

      if (!parent || visited.has(parent.id)) break;

      hierarchy.unshift(parent.name);
      visited.add(parent.id);
      current = parent;
    }

    return hierarchy.join(" -> ");
  };

  const handleSave = async (submitAfter = false) => {
    // Validate all required fields before starting save
    const validation = validateAll();
    if (!validation.isValid) {
      showWarning(
        `Please fill required field(s): ${validation.missingFields.join(", ")}`,
      );
      return;
    }
    // Ensure schedule start and end are in the future and end is after start
    try {
      const now = new Date();
      let start = null;
      let end = null;
      if (formData.scheduleStart) {
        start = new Date(formData.scheduleStart);
        if (isNaN(start.getTime()) || start <= now) {
          showWarning("Schedule Start Date must be greater than current time");
          return;
        }
      }
      if (formData.scheduleEnd) {
        end = new Date(formData.scheduleEnd);
        if (isNaN(end.getTime()) || end <= now) {
          showWarning("Schedule End Date must be greater than current time");
          return;
        }
      }
      if (start && end && end <= start) {
        showWarning("Schedule End Date must be greater than Schedule Start Date");
        return;
      }
    } catch (e) {
      // if parsing fails, show a warning and abort save
      showWarning("Invalid schedule date/time provided");
      return;
    }
    setLoading(true);

    try {
      // Normalize checklist status: treat checked true or "YES" as DONE
      const normalizeChecklistStatus = (ci) => {
        if (!ci) return "PENDING";
        const checkedFlag = ci.checked === true || ci.checked === "YES";
        const statusStr = ci.status ? String(ci.status).toUpperCase() : "";
        if (checkedFlag || statusStr === "YES" || statusStr === "DONE")
          return "DONE";
        return "PENDING";
      };
      // If creating a new permit, submit entire form in a single multipart request
      if (!isEditing) {
        const formatDateTimeForBackend = (dt) => {
          if (!dt) return null;
          return dt.length === 16 ? `${dt}:00` : dt;
        };

        const fullPayload = {
          permit: {
            // keep legacy single typeId for backward compatibility
            typeId: formData.typeId ? Number(formData.typeId) : null,
            locationId: formData.locationId
              ? Number(formData.locationId)
              : null,
            siteId: formData.siteId ? Number(formData.siteId) : null,
            receiverDepartmentId: formData.receiverDepartmentId
              ? Number(formData.receiverDepartmentId)
              : null,
            issuerDepartmentId: formData.issuerDepartmentId
              ? Number(formData.issuerDepartmentId)
              : null,
            equipmentId: formData.equipmentId
              ? Number(formData.equipmentId)
              : null,
            scheduleStart: formatDateTimeForBackend(formData.scheduleStart),
            scheduleEnd: formatDateTimeForBackend(formData.scheduleEnd),
            jobDescription: formData.jobDescription || null,
            permitAutoRejectOnScheduleTime:
              !!formData.permitAutoRejectOnScheduleTime,
            revalidationRequired: !!formData.revalidationRequired,
            revalidationIntervalHours: formData.revalidationRequired
              ? Number(formData.revalidationIntervalHours) || 8
              : null,
            permitScope: formData.permitScope || "STANDARD",
            contractorName: formData.contractorName || null,
            externalWorkOrderId: formData.externalWorkOrderId || null,
            residualRiskLevel: formData.residualRiskLevel || null,
            priority: formData.priority || "MEDIUM",
            clearanceDepartments: (permitClearanceDepartments || []).map(
              (c) => ({
                id: c.id || null,
                departmentId: c.departmentId ? Number(c.departmentId) : null,
                approved: !!c.approved,
              }),
            ),
          },
          riskAssessment: {
            overallRiskId: riskAssessmentData.overallRisk
              ? Number(riskAssessmentData.overallRisk)
              : null,
          },
          permitTypeBlocks: permitTypeBlocksState.map((b) => ({
            id: b.id,
            permitTypeId: b.permitTypeId ? Number(b.permitTypeId) : null,
            residualRiskLevel: b.residualRiskLevel || null,
            contractorName: b.contractorName || null,
            externalWorkOrderId: b.externalWorkOrderId || null,
            equipmentId:
              b.equipmentId !== undefined &&
                b.equipmentId !== null &&
                b.equipmentId !== ""
                ? b.equipmentId
                : null,
            reviewerRemark: b.reviewerRemark || null,
            approved: !!b.approved,
            competentPersonName: b.competentPersonName || null,
            competentPersonApproved:
              b.competentPersonApproved !== undefined
                ? !!b.competentPersonApproved
                : null,
          })),
          riskAssessmentItems: riskAssessmentData.hazards.map((h, idx) => ({
            hazardId: h.hazardId ? Number(h.hazardId) : null,
            controlIds: getEffectiveControlIds(h).map((c) => Number(c)),
            otherControlText: h.otherControlText || null,
            taskDescription: h.taskDescription || `Step ${idx + 1}`,
            stepNo: idx + 1,
            responsiblePersonId: h.responsiblePersonId ? Number(h.responsiblePersonId) : null,
          })),
          checklistItems: (permitTypeBlocksState || []).flatMap((b) =>
            (b.checklistItems || []).map((ci) => ({
              text: ci.text,
              mandatory: !!ci.mandatory,
              checked: !!ci.checked,
              // send id for backend but prefer checklist object in UI
              permitTypeChecklistId:
                (ci.checklist && ci.checklist.id) || ci.permitTypeChecklistId,
              checklist: ci.checklist || null,
              // for new permits the block may not have a DB id yet; fallback to the
              // selected permitTypeId so the backend can associate checklist items
              // to the correct permit type when the blocks are created server-side.
              // send permitTypeId when available (backend uses permitPermitTypeId as permitTypeId),
              // fallback to block id if permitTypeId is missing
              permitPermitTypeId: b.permitTypeId
                ? Number(b.permitTypeId)
                : b.id || null,
              fields: ci.fields || [],
              fieldsValues: ci.fieldsValues || {},
            })),
          ),
        };

        // Checklist validation removed - no longer required
        const form = new FormData();
        form.append("payload", JSON.stringify(fullPayload));
        attachments.forEach((file) => form.append("files", file));

        const resp = await fetch(
          `${restApiUrl}/api/permits/full?submit=${submitAfter}`,
          {
            method: "POST",
            mode: "cors",
            headers: multipartHeaders(),
            body: form,
          },
        );
        const json = await resp.json();
        if (!json.success)
          throw new Error(json.error || "Failed to save permit");

        showSuccess(
          submitAfter
            ? "Permit created and submitted successfully"
            : "Permit saved as draft successfully",
        );
        setLoading(false);
        navigate("/permits");
        return;
      }
      // Editing: submit entire form in single multipart PUT to /api/permits/full
      if (isEditing) {
        const formatDateTimeForBackend = (dt) => {
          if (!dt) return null;
          return dt.length === 16 ? `${dt}:00` : dt;
        };

        const fullPayload = {
          permit: {
            id: Number(id),
            typeId: formData.typeId ? Number(formData.typeId) : null,
            locationId: formData.locationId
              ? Number(formData.locationId)
              : null,
            siteId: formData.siteId ? Number(formData.siteId) : null,
            receiverDepartmentId: formData.receiverDepartmentId
              ? Number(formData.receiverDepartmentId)
              : null,
            issuerDepartmentId: formData.issuerDepartmentId
              ? Number(formData.issuerDepartmentId)
              : null,
            equipmentId: formData.equipmentId
              ? Number(formData.equipmentId)
              : null,
            scheduleStart: formatDateTimeForBackend(formData.scheduleStart),
            scheduleEnd: formatDateTimeForBackend(formData.scheduleEnd),
            jobDescription: formData.jobDescription || null,
            permitAutoRejectOnScheduleTime:
              !!formData.permitAutoRejectOnScheduleTime,
            revalidationRequired: !!formData.revalidationRequired,
            revalidationIntervalHours: formData.revalidationRequired
              ? Number(formData.revalidationIntervalHours) || 8
              : null,
            permitScope: formData.permitScope || "STANDARD",
            contractorName: formData.contractorName || null,
            externalWorkOrderId: formData.externalWorkOrderId || null,
            residualRiskLevel: formData.residualRiskLevel || null,
            priority: formData.priority || "MEDIUM",
            clearanceDepartments: (permitClearanceDepartments || []).map(
              (c) => ({
                id: c.id || null,
                departmentId: c.departmentId ? Number(c.departmentId) : null,
                approved: !!c.approved,
              }),
            ),
          },
          riskAssessment: {
            overallRiskId: riskAssessmentData.overallRisk
              ? Number(riskAssessmentData.overallRisk)
              : null,
          },
          permitTypeBlocks: permitTypeBlocksState.map((b) => ({
            id: b.id,
            permitTypeId: b.permitTypeId ? Number(b.permitTypeId) : null,
            residualRiskLevel: b.residualRiskLevel || null,
            contractorName: b.contractorName || null,
            externalWorkOrderId: b.externalWorkOrderId || null,
            equipmentId: b.equipmentId ? b.equipmentId : null,
            reviewerRemark: b.reviewerRemark || null,
            approved: !!b.approved,
            competentPersonName: b.competentPersonName || null,
            competentPersonApproved:
              b.competentPersonApproved !== undefined
                ? !!b.competentPersonApproved
                : null,
          })),
          riskAssessmentItems: riskAssessmentData.hazards.map((h, idx) => ({
            hazardId: h.hazardId ? Number(h.hazardId) : null,
            controlIds: getEffectiveControlIds(h).map((c) => Number(c)),
            otherControlText: h.otherControlText || null,
            taskDescription: h.taskDescription || `Step ${idx + 1}`,
            stepNo: idx + 1,
            responsiblePersonId: h.responsiblePersonId ? Number(h.responsiblePersonId) : null,
          })),
          checklistItems: (permitTypeBlocksState || []).flatMap((b) =>
            (b.checklistItems || []).map((ci) => ({
              text: ci.text,
              mandatory: !!ci.mandatory,
              checked: !!ci.checked,
              permitTypeChecklistId:
                (ci.checklist && ci.checklist.id) || ci.permitTypeChecklistId,
              checklist: ci.checklist || null,
              // prefer permitTypeId for backend mapping; fallback to block id
              permitPermitTypeId: b.permitTypeId
                ? Number(b.permitTypeId)
                : b.id || null,
              // include fields and saved field values so backend can persist them
              fields: ci.fields || [],
              fieldsValues: ci.fieldsValues || {},
              id: ci.id || null,
            })),
          ),
        };
        // Checklist validation removed - no longer required
        const form = new FormData();
        form.append("payload", JSON.stringify(fullPayload));
        attachments.forEach((file) => form.append("files", file));

        const willSubmit = submitAfter && permitStatus === "DRAFT";
        const resp = await fetch(
          `${restApiUrl}/api/permits/full?submit=${willSubmit}`,
          {
            method: "PUT",
            mode: "cors",
            headers: multipartHeaders(),
            body: form,
          },
        );
        const json = await resp.json();
        if (!json.success)
          throw new Error(json.error || "Failed to update permit");

        showSuccess(
          willSubmit
            ? "Permit updated and submitted successfully"
            : "Permit updated successfully",
        );
        setLoading(false);
        navigate("/permits");
        return;
      }
    } catch (error) {
      showError(`Error creating permit: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Shared MUI sx overrides so Autocomplete/TextField controls match the pd- design system
  // (compact height, square-ish radius, no floating label — label is rendered externally via <FieldWrap>).

  return (
    <div id="permit-wizard" className="max-w-5xl mx-auto p-2 rounded-xl shadow space-y-6 h-fit">
      <div className="flex flex-col h-[88vh] text-sm">
        <div className="pd-card-head" style={{ borderRadius: "8px 8px 0 0" }}>
          <h2 style={{ fontSize: "17px", textTransform: "none", letterSpacing: "normal", fontWeight: 700, color: "var(--pd-ink)" }}>
            {isEditing ? "Edit Permit" : "Create New Permit"}
          </h2>
          <button
            onClick={() => navigate("/permits")}
            className="pd-btn-add"
            style={{ background: "var(--pd-muted)", width: "auto", padding: "0 12px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600, gap: "6px" }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </button>
        </div>
        {/* Step Indicator */}
        <div
          className="pd-card"
          style={{ borderRadius: 0, borderTop: "none", borderBottom: "none" }}
        >
          <div
            className="flex items-center overflow-x-auto flex-shrink-0"
            style={{ padding: "12px 13px" }}
            ref={stepContainerRef}
          >
            {["Job Details", "Risk Assessment", "Checklist & Attachments", "Review"].map(
              (step, index) => {
                const done = index + 1 < currentStep;
                const active = index + 1 === currentStep;
                return (
                  <div
                    key={index}
                    ref={(el) => (stepRefs.current[index] = el)}
                    className="flex items-center flex-shrink-0"
                  >
                    <div
                      className="pd-pill"
                      style={{
                        width: "22px", height: "22px", borderRadius: "50%", padding: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "11px",
                        background: done ? "var(--pd-ok)" : active ? "var(--pd-accent)" : "var(--pd-surface-2)",
                        color: done || active ? "#fff" : "var(--pd-muted-2)",
                        border: done || active ? "none" : "1px solid var(--pd-line-strong)",
                      }}
                    >
                      {done ? "✓" : index + 1}
                    </div>
                    <span
                      className="ml-2 text-xs"
                      style={{ fontWeight: 600, color: active ? "var(--pd-ink)" : done ? "var(--pd-muted)" : "var(--pd-muted-2)", whiteSpace: "nowrap" }}
                    >
                      {step}
                    </span>
                    {index < 3 && (
                      <div
                        className="hidden md:block"
                        style={{ width: "48px", height: "1px", margin: "0 10px", background: done ? "var(--pd-ok)" : "var(--pd-line-strong)", opacity: done ? 0.5 : 1 }}
                      ></div>
                    )}
                  </div>
                );
              },
            )}
          </div>
        </div>

        {/* Content Area (compact) - bounded scroller so only tab content scrolls */}
        <div className="pd-card flex-1 overflow-y-auto p-2 sm:p-3" style={{ borderRadius: "0 0 8px 8px", borderTop: "none" }}>
          <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
            {/* Step 1: Job Details */}
            {currentStep === 1 && (
              <div className="space-y-2 mt-2">
                {/* Row 1: Site */}
                <div className="pd-kv-grid">
                  <FieldWrap label="Site" required>
                    <Autocomplete
                      size="small"
                      sx={pdControlSx}
                      options={sitesList || []}
                      getOptionLabel={(option) => option.name || ""}
                      value={selectedSite}
                      onChange={(event, newValue) => {
                        setSelectedSite(newValue || null);
                        setSelectedDepartment("");
                        setSelectedIssuerDepartment("");
                        setFormData({
                          ...formData,
                          siteId: newValue?.id || "",
                          receiverDepartmentId: "",
                          issuerDepartmentId: "",
                          locationId: "",
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select site…" />
                      )}
                    />
                  </FieldWrap>
                </div>

                {/* Row 1b: Issuer / Receiver Department */}
                <div className="pd-kv-grid" style={{ marginTop: "12px" }}>
                  <FieldWrap label="Issuer Department" required>
                    <Autocomplete
                      size="small"
                      sx={pdControlSx}
                      options={departments || []}
                      getOptionLabel={(option) =>
                        option.name || option.departmentName || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        String(option?.id) === String(value?.id)
                      }
                      value={
                        departments.find(
                          (d) => String(d.id) === String(selectedIssuerDepartment),
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setSelectedIssuerDepartment(
                          newValue?.id != null ? String(newValue.id) : "",
                        );
                        setFormData({
                          ...formData,
                          issuerDepartmentId:
                            newValue?.id != null ? String(newValue.id) : "",
                        });
                      }}
                      disabled={!selectedSite}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select department…" />
                      )}
                    />
                  </FieldWrap>
                  <FieldWrap label="Receiver Department" required>
                    <Autocomplete
                      size="small"
                      sx={pdControlSx}
                      options={departments || []}
                      getOptionLabel={(option) =>
                        option.name || option.departmentName || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        String(option?.id) === String(value?.id)
                      }
                      value={
                        departments.find(
                          (d) => String(d.id) === String(selectedDepartment),
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setSelectedDepartment(
                          newValue?.id != null ? String(newValue.id) : "",
                        );
                        setFormData({
                          ...formData,
                          receiverDepartmentId:
                            newValue?.id != null ? String(newValue.id) : "",
                        });
                      }}
                      disabled={!selectedSite}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select department…" />
                      )}
                    />
                  </FieldWrap>
                </div>

                {/* Row 2: Location (Full Width) - moved above job description */}
                <div className="pd-kv-grid" style={{ marginTop: "12px" }}>
                  <FieldWrap label="Location" required wide>
                    <Autocomplete
                      size="small"
                      sx={pdControlSx}
                      options={locations}
                      getOptionLabel={(option) =>
                        buildLocationHierarchy(option) || option.name || ""
                      }
                      value={
                        locations.find((l) => String(l.id) === String(formData.locationId)) || null
                      }
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          locationId: newValue?.id || "",
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select location…" />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                    />
                  </FieldWrap>

                  {/* Row 3: Schedule Start, Schedule End (immediately after location) */}
                  <FieldWrap label="Schedule Start" required>
                    <TextField
                      type="datetime-local"
                      value={formData.scheduleStart}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          scheduleStart: e.target.value,
                        })
                      }
                      fullWidth
                      size="small"
                      sx={pdControlSx}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </FieldWrap>
                  <FieldWrap label="Schedule End" required>
                    <TextField
                      type="datetime-local"
                      value={formData.scheduleEnd}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          scheduleEnd: e.target.value,
                        })
                      }
                      fullWidth
                      size="small"
                      sx={pdControlSx}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </FieldWrap>

                  {/* Row 4: Job Description (Full Width) - placed after schedule */}
                  <FieldWrap label="Job Description" required wide>
                    <TextField
                      placeholder="Enter job description"
                      value={formData.jobDescription}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          jobDescription: e.target.value,
                        })
                      }
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      sx={pdControlSx}
                      required
                    />
                  </FieldWrap>
                </div>

                <div className="mt-1">
                  {/* </FormControl> */}
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      id="autoReject"
                      type="checkbox"
                      checked={!!formData.permitAutoRejectOnScheduleTime}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          permitAutoRejectOnScheduleTime: e.target.checked,
                        })
                      }
                      className="h-4 w-4"
                      style={{ accentColor: "var(--pd-accent)" }}
                    />
                    <label
                      htmlFor="autoReject"
                      className="text-sm"
                      style={{ color: "var(--pd-ink)" }}
                    >
                      Auto reject on scheduled time
                    </label>
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <input
                      id="revalidationRequired"
                      type="checkbox"
                      checked={!!formData.revalidationRequired}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          revalidationRequired: e.target.checked,
                          revalidationIntervalHours: e.target.checked
                            ? (formData.revalidationIntervalHours || 8)
                            : "",
                        })
                      }
                      className="h-4 w-4"
                      style={{ accentColor: "var(--pd-accent)" }}
                    />
                    <label
                      htmlFor="revalidationRequired"
                      className="text-sm"
                      style={{ color: "var(--pd-ink)" }}
                    >
                      Requires periodic revalidation
                    </label>
                  </div>
                  {formData.revalidationRequired && (
                    <div className="mt-2 ml-6" style={{ maxWidth: "220px" }}>
                      <label className="pd-field-label" style={{ display: "block", marginBottom: "4px" }}>
                        Revalidation Interval
                      </label>
                      <select
                        value={formData.revalidationIntervalHours || 8}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            revalidationIntervalHours: Number(e.target.value),
                          })
                        }
                        style={{ width: "100%", height: "26px", padding: "0 8px", border: "1px solid var(--pd-line-strong)", borderRadius: "6px", background: "var(--pd-surface)", fontSize: "12.5px", color: "var(--pd-ink)" }}
                      >
                        {[8, 12, 16, 24, 36, 48, 72].map((h) => (
                          <option key={h} value={h}>
                            Every {h} hours
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Row 5: Permit Types - shown after job description */}
                <div style={{ marginTop: "20px" }}>
                  <h3 className="pd-section-title">
                    Permit Types<span style={{ color: "var(--pd-crit)" }}>*</span>
                  </h3>
                  <div className="mb-2">
                    {permitTypeBlocksState &&
                      permitTypeBlocksState.length > 0 ? (
                      permitTypeBlocksState.map((b, idx) => (
                        <div
                          key={idx}
                          className="pd-card"
                          style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px", padding: "10px 12px", marginBottom: "8px" }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: "13.5px" }}>
                              {b.permitTypeName ||
                                (
                                  permitTypes.find(
                                    (pt) =>
                                      String(pt.id) === String(b.permitTypeId),
                                  ) || {}
                                ).name}
                              {b.residualRiskLevel && (
                                <span className={`pd-pill ${b.residualRiskLevel === "HIGH" ? "pd-pill-crit" : b.residualRiskLevel === "MEDIUM" ? "pd-pill-warn" : "pd-pill-ok"}`} style={{ marginLeft: "8px" }}>
                                  {b.residualRiskLevel} risk
                                </span>
                              )}
                            </div>
                            <div className="text-xs" style={{ color: "var(--pd-muted)", marginTop: "3px" }}>
                              <span style={{ marginRight: "12px" }}>Contractor: {b.contractorName || "—"}</span>
                              <span style={{ marginRight: "12px" }}>Work Order ID: {b.externalWorkOrderId || "—"}</span>
                              <span>Equipment: {b.equipmentId || "—"}</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                            <button
                              type="button"
                              disabled={loading}
                              onClick={() => {
                                const block =
                                  permitTypeBlocksState[idx] || {};
                                setNewPermitTypeForm({
                                  permitTypeId: block.permitTypeId || "",
                                  residualRiskLevel:
                                    block.residualRiskLevel || "",
                                  contractorName: block.contractorName || "",
                                  externalWorkOrderId:
                                    block.externalWorkOrderId || "",
                                  equipmentId: block.equipmentId || "",
                                  reviewerRemark: block.reviewerRemark || "",
                                  approved: !!block.approved,
                                  checklistItems: block.checklistItems || [],
                                });
                                setEditingPermitIndex(idx);
                                setAddingPermitType(true);
                                setAddFullWidth(true);
                              }}
                              className="pd-btn-add"
                              style={{ background: "var(--pd-warn)", borderRadius: "6px" }}
                              aria-label="Edit permit type"
                              title="Edit permit type"
                            >
                              <FiEdit size={13} />
                            </button>
                            <button
                              type="button"
                              disabled={loading}
                              onClick={() => {
                                setPermitTypeBlocksState((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                );
                                if (editingPermitIndex === idx) {
                                  setEditingPermitIndex(null);
                                  setAddingPermitType(false);
                                  setAddFullWidth(false);
                                  setNewPermitTypeForm({
                                    permitTypeId: "",
                                    residualRiskLevel: "",
                                    contractorName: "",
                                    externalWorkOrderId: "",
                                    equipmentId: "",
                                    reviewerRemark: "",
                                    approved: false,
                                  });
                                }
                              }}
                              className="pd-btn-add"
                              style={{ background: "var(--pd-crit)", borderRadius: "6px" }}
                              aria-label="Remove permit type"
                              title="Remove permit type"
                            >
                              <FiTrash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm" style={{ color: "var(--pd-muted-2)", fontStyle: "italic" }}>
                        No permit types added yet.
                      </div>
                    )}
                  </div>
                  {!addingPermitType ? (
                    <button
                      type="button"
                      className="pd-btn-add"
                      style={{ background: "var(--pd-ok)", width: "auto", padding: "7px 14px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600, gap: "6px" }}
                      onClick={() => {
                        setEditingPermitIndex(null);
                        setAddingPermitType(true);
                        setAddFullWidth(true);

                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Add Permit Type
                    </button>
                  ) : (
                    addingPermitType &&
                    addFullWidth && (
                      <div className="pd-card" style={{ borderStyle: "dashed", background: "var(--pd-surface-2)", padding: "14px", marginBottom: "16px" }}>
                        <h3 className="pd-section-title" style={{ marginTop: 0 }}>Add Permit Type</h3>
                        <div className="pd-kv-grid" style={{ marginBottom: "10px" }}>
                          <FieldWrap label="Permit Type">
                            <FormControl fullWidth size="small" variant="outlined" sx={pdControlSx}>
                              <Select
                                value={newPermitTypeForm.permitTypeId}
                                displayEmpty
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const pt = permitTypes.find(
                                    (t) => String(t.id) === String(val),
                                  );
                                  setNewPermitTypeForm((prev) => ({
                                    ...prev,
                                    permitTypeId: val,
                                    checklistItems: [],
                                  }));

                                  const applyList = (mcs) => {
                                    const checklistItems = (mcs || []).map((mc) => {
                                      if (typeof mc === "string") {
                                        return {
                                          text: mc,
                                          mandatory: false,
                                          checked: false,
                                          status: "PENDING",
                                          fields: [],
                                          fieldsValues: {},
                                        };
                                      }
                                      return {
                                        text:
                                          mc.checklist || mc.text || mc.name || "",
                                        permitTypeChecklistId: mc.id,
                                        mandatory: false,
                                        checked: false,
                                        status: "PENDING",
                                        fields: mc.fields || [],
                                        fieldsValues: {},
                                      };
                                    });
                                    setNewPermitTypeForm((prev) => ({
                                      ...prev,
                                      checklistItems,
                                    }));
                                  };
                                  if (val) {
                                    (async () => {
                                      const mcs =
                                        await fetchPermitTypeMasterChecklists(val);
                                      applyList(mcs);
                                    })();
                                  }
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select Permit Type</em>
                                </MenuItem>
                                {(permitTypes || [])
                                  .filter((type) => {
                                    const already = (
                                      permitTypeBlocksState || []
                                    ).some(
                                      (b) =>
                                        String(b.permitTypeId) === String(type.id),
                                    );
                                    if (!already) return true;
                                    if (
                                      editingPermitIndex != null &&
                                      String(
                                        (
                                          permitTypeBlocksState[
                                          editingPermitIndex
                                          ] || {}
                                        ).permitTypeId,
                                      ) === String(type.id)
                                    )
                                      return true;
                                    return false;
                                  })
                                  .map((type) => (
                                    <MenuItem key={type.id} value={String(type.id)}>
                                      {type.name}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </FieldWrap>
                          <FieldWrap label="Residual Risk">
                            <FormControl fullWidth size="small" variant="outlined" sx={pdControlSx}>
                              <Select
                                value={newPermitTypeForm.residualRiskLevel}
                                displayEmpty
                                onChange={(e) =>
                                  setNewPermitTypeForm({
                                    ...newPermitTypeForm,
                                    residualRiskLevel: e.target.value,
                                  })
                                }
                              >
                                <MenuItem value="">
                                  <em>Select residual risk…</em>
                                </MenuItem>
                                <MenuItem value="LOW">Low</MenuItem>
                                <MenuItem value="MEDIUM">Medium</MenuItem>
                                <MenuItem value="HIGH">High</MenuItem>
                                <MenuItem value="CRITICAL">Critical</MenuItem>
                              </Select>
                            </FormControl>
                          </FieldWrap>

                          <FieldWrap label="Contractor" wide>
                            <FormControl fullWidth size="small" variant="outlined" sx={pdControlSx}>
                              <Select
                                displayEmpty
                                value={newPermitTypeForm.contractorName}
                                onChange={(e) =>
                                  setNewPermitTypeForm({
                                    ...newPermitTypeForm,
                                    contractorName: e.target.value,
                                  })
                                }
                              >
                                <MenuItem value="">— No contractor —</MenuItem>
                                {approvedContractors.map((c) => (
                                  <MenuItem key={c.id} value={c.companyName}>
                                    {c.companyName}
                                    {!c.siteAuthValid && " ⚠ Auth Expired"}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </FieldWrap>
                          <FieldWrap label="External Work Order ID">
                            <TextField
                              placeholder="Optional"
                              value={newPermitTypeForm.externalWorkOrderId}
                              onChange={(e) =>
                                setNewPermitTypeForm({
                                  ...newPermitTypeForm,
                                  externalWorkOrderId: e.target.value,
                                })
                              }
                              size="small"
                              fullWidth
                              sx={pdControlSx}
                            />
                          </FieldWrap>
                          <FieldWrap label="Equipment ID">
                            <TextField
                              placeholder="Optional"
                              value={newPermitTypeForm.equipmentId}
                              onChange={(e) =>
                                setNewPermitTypeForm({
                                  ...newPermitTypeForm,
                                  equipmentId: e.target.value,
                                })
                              }
                              size="small"
                              fullWidth
                              sx={pdControlSx}
                            />
                          </FieldWrap>
                        </div>

                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              if (editingPermitIndex != null) {
                                setPermitTypeBlocksState((prev) =>
                                  prev.map((p, i) =>
                                    i === editingPermitIndex
                                      ? {
                                        ...p,
                                        ...newPermitTypeForm,
                                        permitTypeName: (
                                          permitTypes.find(
                                            (pt) =>
                                              String(pt.id) ===
                                              String(
                                                newPermitTypeForm.permitTypeId,
                                              ),
                                          ) || {}
                                        ).name,
                                      }
                                      : p,
                                  ),
                                );
                                setEditingPermitIndex(null);
                              } else {
                                // prevent duplicate permit types when adding
                                if (
                                  (permitTypeBlocksState || []).some(
                                    (b) =>
                                      String(b.permitTypeId) ===
                                      String(newPermitTypeForm.permitTypeId),
                                  )
                                ) {
                                  showInfo(
                                    "This permit type has already been added.",
                                  );
                                } else {
                                  setPermitTypeBlocksState((prev) => [
                                    ...prev,
                                    {
                                      ...newPermitTypeForm,
                                      permitTypeName: (
                                        permitTypes.find(
                                          (pt) =>
                                            String(pt.id) ===
                                            String(
                                              newPermitTypeForm.permitTypeId,
                                            ),
                                        ) || {}
                                      ).name,
                                    },
                                  ]);
                                }
                              }
                              setNewPermitTypeForm({
                                permitTypeId: "",
                                residualRiskLevel: "",
                                contractorName: "",
                                externalWorkOrderId: "",
                                equipmentId: "",
                                reviewerRemark: "",
                                approved: false,
                                checklistItems: [],
                                clearanceDepartments: [],
                              });
                              setAddingPermitType(false);
                              setAddFullWidth(false);
                            }}
                            className="pd-btn-add"
                            style={{ background: "var(--pd-accent)", width: "auto", padding: "7px 16px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600 }}
                          >
                            {editingPermitIndex != null ? "Save" : "Add Permit Type"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setAddingPermitType(false);
                              setAddFullWidth(false);
                              setNewPermitTypeForm({
                                permitTypeId: "",
                                residualRiskLevel: "",
                                contractorName: "",
                                externalWorkOrderId: "",
                                equipmentId: "",
                                reviewerRemark: "",
                                approved: false,
                                checklistItems: [],
                                clearanceDepartments: [],
                              });
                              setEditingPermitIndex(null);
                            }}
                            className="pd-btn-add"
                            style={{ background: "var(--pd-muted)", width: "auto", padding: "7px 16px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600 }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>

                {/* Row 6: Priority (just before clearance departments) */}
                <div className="pd-kv-grid" style={{ marginTop: "16px" }}>
                  <FieldWrap label="Priority" required>
                    <FormControl fullWidth size="small" variant="outlined" sx={pdControlSx}>
                      <Select
                        value={formData.priority}
                        onChange={(e) =>
                          setFormData({ ...formData, priority: e.target.value })
                        }
                        required
                      >
                        <MenuItem value="LOW">Low</MenuItem>
                        <MenuItem value="MEDIUM">Medium</MenuItem>
                        <MenuItem value="HIGH">High</MenuItem>
                        <MenuItem value="URGENT">Urgent</MenuItem>
                      </Select>
                    </FormControl>
                  </FieldWrap>
                </div>

                {/* Row 7: Clearance obtained from Department/s (after priority) */}
                <div style={{ marginTop: "16px" }}>
                  <div className="mb-4">
                    <h3 className="pd-section-title">Clearance from Departments</h3>
                    <div className="flex gap-2">
                      <div style={{ flex: 1 }}>
                        <Autocomplete fullWidth size="small" variant="outlined"
                          sx={pdControlSx}
                          options={departments || []}
                          getOptionLabel={(option) => option.name || ""}
                          value={
                            departments.find(
                              (d) => String(d.id) === String(selectedClearanceDepartment)
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            setSelectedClearanceDepartment(newValue?.id || "");
                          }}
                          disabled={!selectedSite}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select department to add…"
                            />
                          )}
                        />
                      </div>
                      <button
                        type="button"
                        className="pd-btn-add"
                        style={{ background: "var(--pd-ok)", width: "auto", padding: "0 14px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600, gap: "6px" }}
                        onClick={() => {
                          if (!selectedClearanceDepartment) return;
                          const exists = (
                            permitClearanceDepartments || []
                          ).some(
                            (c) =>
                              String(c.departmentId) ===
                              String(selectedClearanceDepartment),
                          );
                          if (exists) return;
                          const dept = departments.find(
                            (d) =>
                              String(d.id) ===
                              String(selectedClearanceDepartment),
                          );
                          setPermitClearanceDepartments((prev) => [
                            ...(prev || []),
                            {
                              id: null,
                              departmentId: String(selectedClearanceDepartment),
                              departmentName: dept ? dept.name : "",
                              approved: false,
                            },
                          ]);
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        Add
                      </button>
                    </div>

                    <div className="mt-2">
                      {(permitClearanceDepartments || []).length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {(permitClearanceDepartments || []).map((c, idx) => (
                            <div
                              key={idx}
                              className="pd-card"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "7px 10px",
                                background: "var(--pd-surface-2)",
                              }}
                            >
                              <span style={{ fontSize: "12.5px", fontWeight: 600, color: "var(--pd-ink)" }}>
                                {c.departmentName || c.departmentId}
                              </span>
                              <button
                                type="button"
                                disabled={loading}
                                onClick={() => {
                                  setPermitClearanceDepartments((prev) =>
                                    (prev || []).filter((_, i) => i !== idx),
                                  );
                                }}
                                aria-label="Remove clearance"
                                className="pd-btn-add"
                                style={{ background: "var(--pd-crit)", flexShrink: 0 }}
                              >
                                <FiTrash2 size={11} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: "12.5px", color: "var(--pd-muted-2)", fontStyle: "italic" }}>
                          No clearance departments added for this permit.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Risk Assessment */}
            {currentStep === 2 && (
              <div className="space-y-3">
                <h3 className="pd-section-title" style={{ marginTop: 0 }}>
                  Job Steps, Hazards &amp; Control Measures
                </h3>
                {riskAssessmentData.hazards.map((item, index) => (
                  <div key={index} className="pd-card" style={{ borderLeft: "3px solid var(--pd-info)", position: "relative" }}>
                    <div className="pd-card-body">
                      {/* Step header */}
                      <div className="flex items-center gap-2 mb-3 pr-10">
                        <span className="pd-pill pd-pill-info">Step {index + 1}</span>
                      </div>
                      {/* Task description */}
                      <div className="mb-3 pd-field">
                        <label className="pd-field-label" style={{ display: "block", marginBottom: "4px" }}>Task / Job Step Description</label>
                        <input
                          type="text"
                          placeholder="Describe the task or job step..."
                          value={item.taskDescription || ""}
                          onChange={(e) => {
                            const newHazards = [...riskAssessmentData.hazards];
                            newHazards[index].taskDescription = e.target.value;
                            setRiskAssessmentData({ ...riskAssessmentData, hazards: newHazards });
                          }}
                          style={{ width: "100%", height: "30px", padding: "0 8px", border: "1px solid var(--pd-line-strong)", borderRadius: "6px", fontSize: "12.5px", background: "var(--pd-surface)", color: "var(--pd-ink)" }}
                        />
                      </div>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => {
                          const newHazards = riskAssessmentData.hazards.filter(
                            (_, i) => i !== index,
                          );
                          setRiskAssessmentData({
                            ...riskAssessmentData,
                            hazards: newHazards,
                          });
                        }}
                        title="Remove step"
                        aria-label={`Remove step ${index + 1}`}
                        className="pd-btn-add"
                        style={{ position: "absolute", top: "14px", right: "14px", background: "var(--pd-crit)" }}
                      >
                        <FiTrash2 size={12} />
                      </button>

                      <div className="mb-3 pd-field">
                        <label className="pd-field-label" style={{ display: "block", marginBottom: "4px" }}>Hazard</label>
                        <div className="relative">
                          <input
                            type="text"
                            readOnly
                            value={
                              hazards.find((h) => String(h.id) === item.hazardId)?.name || ""
                            }
                            onClick={() => setOpenHazardIndex(index)}
                            placeholder="Select a hazard..."
                            style={{ width: "100%", height: "30px", padding: "0 8px", border: "1px solid var(--pd-line-strong)", borderRadius: "6px", background: "var(--pd-surface)", cursor: "pointer", fontSize: "12.5px", color: "var(--pd-ink)" }}
                          />

                          {openHazardIndex === index && (
                            <div
                              className="absolute left-0 right-0 top-full mt-2 bg-white border rounded max-h-56 overflow-y-auto z-50 shadow-lg"
                              onScroll={handleHazardsScroll}
                            >
                              {hazards.map((h) => (
                                <div
                                  key={h.id}
                                  className="p-2 hover:bg-gray-100 cursor-pointer"
                                  onClick={() => {
                                    const newHazards = [...riskAssessmentData.hazards];
                                    newHazards[index].hazardId = String(h.id);

                                    const mandatoryIds = controlMeasures
                                      .filter((cm) => String(cm.hazardId) === String(h.id) && cm.mandatory)
                                      .map((cm) => String(cm.id));
                                    const existingIds = newHazards[index].controlIds || [];
                                    newHazards[index].controlIds = Array.from(
                                      new Set([...existingIds, ...mandatoryIds]),
                                    );

                                    setRiskAssessmentData({
                                      ...riskAssessmentData,
                                      hazards: newHazards,
                                    });

                                    setOpenHazardIndex(null);
                                  }}
                                >
                                  {h.name}
                                </div>
                              ))}

                              {hazardsLoading && (
                                <div className="p-2 flex items-center justify-center">
                                  <svg
                                    className="animate-spin h-5 w-5 text-gray-600"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                    ></path>
                                  </svg>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mb-3 pd-field">
                        <label className="pd-field-label" style={{ display: "block", marginBottom: "4px" }}>Control Measures</label>
                        {item.hazardId && (() => {
                          const hazardControls = controlMeasures.filter((cm) => String(cm.hazardId) === item.hazardId);
                          const allSelected = hazardControls.length > 0 && hazardControls.every((cm) => item.controlIds.includes(String(cm.id)) || !!cm.mandatory);
                          return (
                            <div className="flex items-center mb-1.5 gap-2">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={(e) => {
                                  const newHazards = [...riskAssessmentData.hazards];
                                  if (e.target.checked) {
                                    const allIds = hazardControls.map((cm) => String(cm.id));
                                    newHazards[index].controlIds = Array.from(new Set([...newHazards[index].controlIds, ...allIds]));
                                  } else {
                                    const nonMandatoryIds = hazardControls.filter((cm) => !cm.mandatory).map((cm) => String(cm.id));
                                    newHazards[index].controlIds = newHazards[index].controlIds.filter((id) => !nonMandatoryIds.includes(String(id)));
                                  }
                                  setRiskAssessmentData({ ...riskAssessmentData, hazards: newHazards });
                                }}
                                style={{ accentColor: "var(--pd-accent)" }}
                              />
                              <span style={{ fontSize: "12.5px", color: "var(--pd-ink)", fontWeight: 600 }}>Select All</span>
                            </div>
                          );
                        })()}
                        <div
                          style={{ maxHeight: "160px", overflowY: "auto", border: "1px solid var(--pd-line)", borderRadius: "6px", padding: "8px", background: "var(--pd-surface-2)" }}
                          onScroll={handleControlsScroll}
                        >
                          {item.hazardId ? controlMeasures
                            .filter((cm) => String(cm.hazardId) === item.hazardId)
                            .map((cm) => (
                              <div key={cm.id} className="flex items-center mb-1.5 gap-2">
                                <input
                                  type="checkbox"
                                  checked={item.controlIds.includes(String(cm.id)) || !!cm.mandatory}
                                  disabled={!!cm.mandatory}
                                  onChange={(e) => {
                                    const newHazards = [...riskAssessmentData.hazards];
                                    const controlId = String(cm.id);
                                    if (e.target.checked) {
                                      if (!newHazards[index].controlIds.includes(controlId)) {
                                        newHazards[index].controlIds.push(controlId);
                                      }
                                    } else {
                                      newHazards[index].controlIds = newHazards[index].controlIds.filter((id) => String(id) !== controlId);
                                    }
                                    setRiskAssessmentData({ ...riskAssessmentData, hazards: newHazards });
                                  }}
                                  style={{ accentColor: "var(--pd-accent)" }}
                                />
                                <span style={{ fontSize: "12.5px", color: "var(--pd-ink)" }}>
                                  {cm.name}
                                  {cm.mandatory && (
                                    <span className="pd-pill pd-pill-crit" style={{ marginLeft: "6px" }}>Mandatory</span>
                                  )}
                                </span>
                              </div>
                            )) : <p style={{ fontSize: "12.5px", color: "var(--pd-muted-2)", fontStyle: "italic" }}>Select a hazard first to see control measures.</p>}
                          {item.hazardId && (
                            <div className="flex items-center mb-1.5 gap-2">
                              <input
                                type="checkbox"
                                checked={item.otherControlText !== undefined && item.otherControlText !== null}
                                onChange={(e) => {
                                  const newHazards = [...riskAssessmentData.hazards];
                                  if (e.target.checked) {
                                    newHazards[index].otherControlText = "";
                                  } else {
                                    newHazards[index].otherControlText = undefined;
                                  }
                                  setRiskAssessmentData({ ...riskAssessmentData, hazards: newHazards });
                                }}
                                style={{ accentColor: "var(--pd-accent)" }}
                              />
                              <span style={{ fontSize: "12.5px", color: "var(--pd-ink)" }}>Other</span>
                            </div>
                          )}
                          {item.hazardId && item.otherControlText !== undefined && item.otherControlText !== null && (
                            <input
                              type="text"
                              placeholder="Specify other control measure..."
                              value={item.otherControlText || ""}
                              onChange={(e) => {
                                const newHazards = [...riskAssessmentData.hazards];
                                newHazards[index].otherControlText = e.target.value;
                                setRiskAssessmentData({ ...riskAssessmentData, hazards: newHazards });
                              }}
                              style={{ width: "100%", border: "1px solid var(--pd-line-strong)", borderRadius: "6px", padding: "5px 8px", fontSize: "12.5px", marginTop: "4px", background: "var(--pd-surface)", color: "var(--pd-ink)" }}
                            />
                          )}
                        </div>
                      </div>

                      <div className="mb-1 pd-field">
                        <label className="pd-field-label" style={{ display: "block", marginBottom: "4px" }}>Responsible Person<span style={{ color: "var(--pd-crit)", marginLeft: "2px" }}>*</span></label>
                        {(() => {
                          const empOptions = [...employees];
                          if (item.responsiblePersonId && item.responsiblePersonName && !employees.find((e) => String(e.id) === String(item.responsiblePersonId))) {
                            empOptions.unshift({ id: Number(item.responsiblePersonId), employeeName: item.responsiblePersonName, empCode: "" });
                          }
                          return (
                            <Autocomplete
                              size="small"
                              sx={pdControlSx}
                              options={empOptions}
                              getOptionLabel={(opt) => opt ? `${opt.empCode ? opt.empCode + ' - ' : ''}${opt.employeeName || ''}`.trim() : ""}
                              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                              value={empOptions.find((e) => String(e.id) === String(item.responsiblePersonId || "")) || null}
                              onChange={(_, newVal) => {
                                const newHazards = [...riskAssessmentData.hazards];
                                newHazards[index].responsiblePersonId = newVal ? String(newVal.id) : "";
                                newHazards[index].responsiblePersonName = newVal ? `${newVal.empCode ? newVal.empCode + ' - ' : ''}${newVal.employeeName || ''}`.trim() : "";
                                setRiskAssessmentData({ ...riskAssessmentData, hazards: newHazards });
                              }}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Search employee..." variant="outlined" size="small" />
                              )}
                            />
                          );
                        })()}
                      </div>

                    </div>
                  </div>
                ))}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      setRiskAssessmentData({
                        ...riskAssessmentData,
                        hazards: [
                          ...riskAssessmentData.hazards,
                          { hazardId: "", controlIds: [], otherControlText: undefined, taskDescription: "", responsiblePersonId: "", responsiblePersonName: "" },
                        ],
                      })
                    }
                    className="pd-btn-add"
                    style={{ background: "var(--pd-accent)", width: "auto", padding: "7px 14px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600, gap: "6px" }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add Step
                  </button>
                </div>
                <div className="pd-field">
                  <label className="pd-field-label" style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                    Overall Risk<span style={{ color: "var(--pd-crit)" }}>*</span>
                    <RiskMatrixInfo
                      riskMatrices={riskMatrices}
                      onSelectRisk={(id) =>
                        setRiskAssessmentData({
                          ...riskAssessmentData,
                          overallRisk: String(id),
                        })
                      }
                    />
                  </label>
                  <FormControl fullWidth size="small" variant="outlined" sx={pdControlSx}>
                    <Select
                      value={riskAssessmentData.overallRisk || ""}
                      onChange={(e) =>
                        setRiskAssessmentData({
                          ...riskAssessmentData,
                          overallRisk: e.target.value,
                        })
                      }
                      displayEmpty
                      required
                    >
                      <MenuItem value="">
                        <em>Select Risk</em>
                      </MenuItem>
                      {riskMatrices.map((rm) => (
                        <MenuItem key={rm.id} value={String(rm.id)}>
                          {`Likelihood: ${rm.likelihood}, Severity: ${rm.severity} (${rm.riskLevel})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            )}

            {/* Step 3: Attachments (includes Checklist) */}
            {currentStep === 3 && (
              <div className="space-y-3">
                <h3 className="pd-section-title" style={{ marginTop: 0 }}>Checklist</h3>
                {(permitTypeBlocksState || []).map((b, idx) => (
                  <div key={b.id || idx} className="pd-card">
                    <div className="pd-card-head flex items-center justify-between">
                      <h3 style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--pd-ink)", textTransform: "none", letterSpacing: "normal" }}>
                        {b.permitTypeName || `Permit Type ${idx + 1}`}
                      </h3>
                      {(b.checklistItems || []).length > 0 && (
                        <label className="flex items-center gap-1.5" style={{ cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={(b.checklistItems || []).every((ci) => ci.checked === true || ci.checked === "YES")}
                            onChange={(e) => {
                              const isYes = e.target.checked;
                              setPermitTypeBlocksState((prev) => {
                                const blocks = [...(prev || [])];
                                const block = { ...(blocks[idx] || {}) };
                                block.checklistItems = (block.checklistItems || []).map((item) => ({ ...item, checked: isYes, mandatory: isYes, status: isYes ? "DONE" : "PENDING", fieldsValues: (item && item.fieldsValues) || {} }));
                                blocks[idx] = block;
                                return blocks;
                              });
                            }}
                            style={{ accentColor: "var(--pd-accent)" }}
                          />
                          <span style={{ fontSize: "12.5px", fontWeight: 600, color: "var(--pd-ink)" }}>Select All</span>
                        </label>
                      )}
                    </div>
                    <div className="pd-card-body">
                      {(b.checklistItems || []).map((ci, cidx) => (
                        <div
                          key={cidx}
                          style={{
                            padding: "8px 0",
                            borderTop: cidx > 0 ? "1px solid var(--pd-line)" : "none",
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={ci.checked === true || ci.checked === "YES"}
                              onChange={(e) => {
                                const isYes = e.target.checked;
                                setPermitTypeBlocksState((prev) => {
                                  const blocks = [...(prev || [])];
                                  const block = { ...(blocks[idx] || {}) };
                                  block.checklistItems = (block.checklistItems || []).map((item, ii) => ii === cidx ? { ...item, checked: isYes, mandatory: isYes, status: isYes ? "DONE" : "PENDING", fieldsValues: (item && item.fieldsValues) || {} } : item);
                                  blocks[idx] = block;
                                  return blocks;
                                });
                              }}
                              className="flex-shrink-0"
                              style={{ accentColor: "var(--pd-accent)" }}
                            />
                            <div className="flex-1 flex items-center flex-wrap gap-2">
                              <div style={{ fontSize: "13px", fontWeight: ci.mandatory ? 700 : 500, color: "var(--pd-ink)" }}>
                                {`${cidx + 1}. `}{ci.text}
                                {/* {(ci.checked === true || ci.checked === "YES") && (
                                  <span className="pd-pill pd-pill-ok" style={{ marginLeft: "6px" }}>Yes</span>
                                )} */}
                                {ci.mandatory && (<span className="pd-pill pd-pill-crit" style={{ marginLeft: "6px" }}>Required</span>)}
                              </div>
                              {/* fields: show only when checklist is checked/YES */}
                              {(ci.checked === true || ci.checked === "YES") && ci.fields && ci.fields.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5 items-center">
                                  {(ci.fields || []).map((f) => {
                                    const fv = ci.fieldsValues || {};
                                    const tryKeys = [f.fieldName, f.label, (f.fieldName || "").toLowerCase(), (f.label || "").toLowerCase()];
                                    let val = undefined;
                                    for (const k of tryKeys) {
                                      if (k !== undefined && fv[k] !== undefined) { val = fv[k]; break; }
                                    }
                                    if (val === undefined && fv && typeof fv === "object") {
                                      if (f.label && fv[f.label] !== undefined) val = fv[f.label];
                                    }
                                    return (
                                      <div
                                        key={f.id || f.fieldName}
                                        className="inline-flex items-center gap-1"
                                        style={{ fontSize: "11.5px", background: "var(--pd-surface-2)", border: "1px solid var(--pd-line)", padding: "3px 8px", borderRadius: "999px", width: "fit-content" }}
                                      >
                                        <span style={{ fontWeight: 600, color: "var(--pd-muted-2)" }}>{f.label || f.fieldName}:</span>
                                        {f.fieldType === "BOOLEAN" ? (
                                          <select
                                            value={val !== undefined && val !== null ? String(val) : ""}
                                            onChange={(e) => {
                                              const v = e.target.value;
                                              setPermitTypeBlocksState((prev) => {
                                                const blocks = [...(prev || [])];
                                                const block = { ...(blocks[idx] || {}) };
                                                const items = (block.checklistItems || []).map((it, ii) => ii === cidx ? { ...(it || {}), fieldsValues: { ...((it && it.fieldsValues) || {}), [f.fieldName]: v } } : it);
                                                block.checklistItems = items;
                                                blocks[idx] = block;
                                                return blocks;
                                              });
                                            }}
                                            style={{ border: "none", background: "transparent", fontSize: "11.5px", color: "var(--pd-ink)" }}
                                          >
                                            <option value="">Select</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                          </select>
                                        ) : (
                                          <input
                                            type={f.fieldType === "NUMBER" ? "number" : "text"}
                                            value={val || ""}
                                            onChange={(e) => {
                                              const v = e.target.value;
                                              setPermitTypeBlocksState((prev) => {
                                                const blocks = [...(prev || [])];
                                                const block = { ...(blocks[idx] || {}) };
                                                const items = (block.checklistItems || []).map((it, ii) => ii === cidx ? { ...(it || {}), fieldsValues: { ...((it && it.fieldsValues) || {}), [f.fieldName]: v } } : it);
                                                block.checklistItems = items;
                                                blocks[idx] = block;
                                                return blocks;
                                              });
                                            }}
                                            style={{ border: "none", borderBottom: "1px solid var(--pd-line-strong)", background: "transparent", padding: "0 2px", width: `${Math.max(8, String(val ?? "").length + 2)}ch`, fontSize: "11.5px", color: "var(--pd-ink)" }}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <h3 className="pd-section-title">Upload Attachments</h3>
                <label
                  htmlFor="permit-wizard-attach-input"
                  className="pd-card"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    borderStyle: "dashed",
                    padding: "20px",
                    cursor: "pointer",
                    background: "var(--pd-surface-2)",
                    textAlign: "center",
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} style={{ color: "var(--pd-accent)" }} />
                  <span style={{ fontSize: "12.5px", fontWeight: 600, color: "var(--pd-ink)" }}>Click to upload files</span>
                  <span style={{ fontSize: "11px", color: "var(--pd-muted-2)" }}>or drag and drop attachments here</span>
                  <input
                    id="permit-wizard-attach-input"
                    type="file"
                    multiple
                    onChange={(e) => setAttachments([...e.target.files])}
                    style={{ display: "none" }}
                  />
                </label>
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="inline-flex items-center gap-2"
                        style={{ fontSize: "12px", background: "var(--pd-surface-2)", border: "1px solid var(--pd-line)", padding: "5px 10px", borderRadius: "999px", width: "fit-content" }}
                      >
                        <span style={{ color: "var(--pd-ink)" }}>{file.name}</span>
                        <IconButton
                          type="delete"
                          onClick={() =>
                            setAttachments(
                              attachments.filter((_, i) => i !== index),
                            )
                          }
                          title="Remove attachment"
                          ariaLabel={`Remove attachment ${index}`}
                          className="text-red-600"
                        />
                      </div>
                    ))}
                  </div>
                )}
                {existingAttachments.length > 0 && (
                  <div className="space-y-1.5">
                    {existingAttachments.map((att) => (
                      <div
                        key={att.id}
                        className="pd-card"
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 10px" }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            window.open(
                              `${API_BASE || ""}/api/attachments/${att.id}/download`,
                              "_blank",
                            )
                          }
                          style={{ color: "var(--pd-accent)", fontSize: "12.5px", background: "none", border: "none", cursor: "pointer", textDecoration: "none" }}
                        >
                          {att.fileName}
                        </button>
                        <IconButton
                          type="delete"
                          onClick={async () => {
                            const confirmed = await showConfirm(
                              "Delete attachment?",
                              {
                                title: "Delete Attachment",
                                severity: "error",
                              },
                            );
                            if (!confirmed) return;
                            try {
                              const resp = await fetch(
                                `/api/attachments/${att.id}`,
                                {
                                  method: "DELETE",
                                },
                              );
                              if (!resp.ok) throw new Error("Delete failed");
                              setExistingAttachments(
                                existingAttachments.filter(
                                  (e) => e.id !== att.id,
                                ),
                              );
                            } catch (err) {
                              showError("Failed to delete attachment");
                            }
                          }}
                          title="Delete attachment"
                          ariaLabel={`Delete attachment ${att.id}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-3">
                <h3 className="pd-section-title" style={{ marginTop: 0 }}>Review and Submit</h3>
                <div className="pd-card">
                  <div className="pd-card-head"><h3>Permit Details</h3></div>
                  <div className="pd-card-body pd-kv-grid">
                    <Field label="Site" value={selectedSite?.name || "-"} />
                    <Field
                      label="Receiver Department"
                      value={
                        departments.find(
                          (d) => String(d.id) === String(selectedDepartment),
                        )?.name || "-"
                      }
                    />
                    <Field
                      label="Issuer Department"
                      value={
                        departments.find(
                          (d) => String(d.id) === String(selectedIssuerDepartment),
                        )?.name || "-"
                      }
                    />
                    <Field
                      label="Location"
                      value={
                        buildLocationHierarchy(
                          locations.find(
                            (l) => String(l.id) === String(formData.locationId),
                          ),
                        ) ||
                        locations.find(
                          (l) => String(l.id) === String(formData.locationId),
                        )?.name ||
                        "-"
                      }
                    />
                    <Field
                      label="Schedule Start"
                      value={formData.scheduleStart.replace("T", " ").slice(0, 16)}
                    />
                    <Field
                      label="Schedule End"
                      value={formData.scheduleEnd.replace("T", " ").slice(0, 16)}
                    />
                    <Field label="Priority" value={formData.priority || "-"} />
                    <Field
                      label="Auto reject on scheduled time"
                      value={formData.permitAutoRejectOnScheduleTime ? "Yes" : "No"}
                    />
                    <Field
                      label="Requires periodic revalidation"
                      value={
                        formData.revalidationRequired
                          ? `Yes — every ${formData.revalidationIntervalHours || 8} hours`
                          : "No"
                      }
                    />
                    <Field
                      label="Permit Description"
                      value={formData.jobDescription}
                      className="wide"
                    />
                  </div>
                </div>

                <div className="pd-card">
                  <div className="pd-card-head"><h3>Permit Types</h3></div>
                  <div className="pd-card-body" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {permitTypeBlocksState && permitTypeBlocksState.length > 0 ? (
                      permitTypeBlocksState.map((b, idx) => (
                        <div key={idx} className="pd-card" style={{ background: "var(--pd-surface-2)" }}>
                          <div className="pd-card-body">
                            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                              <span style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--pd-ink)" }}>
                                {b.permitTypeName ||
                                  (
                                    permitTypes.find(
                                      (pt) =>
                                        String(pt.id) === String(b.permitTypeId),
                                    ) || {}
                                  ).name}
                              </span>
                              <span className="pd-pill pd-pill-warn">{b.residualRiskLevel || "-"}</span>
                            </div>
                            <div style={{ fontSize: "11.5px", color: "var(--pd-muted-2)", marginBottom: "8px" }}>
                              Contractor: {b.contractorName || "—"} · Work Order ID: {b.externalWorkOrderId || "—"} · Equipment: {b.equipmentId || "—"}
                            </div>
                            <div>
                              <span className="pd-field-label">Checklist</span>
                              {(b.checklistItems || []).filter(
                                (ci) => ci.checked === true || ci.checked === "YES",
                              ).length === 0 ? (
                                <div style={{ fontSize: "12px", color: "var(--pd-muted-2)", fontStyle: "italic", marginTop: "4px" }}>
                                  No checklist items selected
                                </div>
                              ) : (
                                (b.checklistItems || [])
                                  .filter(
                                    (ci) =>
                                      ci.checked === true || ci.checked === "YES",
                                  )
                                  .map((ci, cidx) => (
                                    <div key={cidx} style={{ marginTop: "6px", fontSize: "12.5px" }}>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span style={{ fontWeight: 600, color: "var(--pd-ink)" }}>
                                          {`${cidx + 1}. `}
                                          {ci.text}
                                        </span>
                                        <span className="pd-pill pd-pill-ok">Yes</span>
                                      </div>
                                      {ci.fields && ci.fields.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-1 ml-4">
                                          {ci.fields.map((f) => {
                                            const fv = ci.fieldsValues || {};
                                            const tryKeys = [
                                              f.fieldName,
                                              f.label,
                                              (f.fieldName || "").toLowerCase(),
                                              (f.label || "").toLowerCase(),
                                            ];
                                            let val = undefined;
                                            for (const k of tryKeys) {
                                              if (
                                                k !== undefined &&
                                                fv[k] !== undefined
                                              ) {
                                                val = fv[k];
                                                break;
                                              }
                                            }
                                            return (
                                              <span
                                                key={f.id || f.fieldName}
                                                style={{ fontSize: "11px", background: "var(--pd-surface)", border: "1px solid var(--pd-line)", padding: "2px 7px", borderRadius: "999px" }}
                                              >
                                                <span style={{ fontWeight: 600, color: "var(--pd-muted-2)", marginRight: "3px" }}>
                                                  {f.label || f.fieldName}:
                                                </span>
                                                {val || "-"}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  ))
                              )}
                            </div>
                            {permitStatus === "PERMIT_TYPE_REVIEW" &&
                              (areClearancesApproved() ? (
                                <div className="mt-2 pd-field">
                                  <label className="pd-field-label" style={{ display: "block", marginBottom: "4px" }}>
                                    Reviewer Remark
                                  </label>
                                  <TextField
                                    value={b.reviewerRemark || ""}
                                    onChange={(e) => {
                                      const blocks = [...permitTypeBlocksState];
                                      blocks[idx].reviewerRemark = e.target.value;
                                      setPermitTypeBlocksState(blocks);
                                    }}
                                    fullWidth
                                    multiline
                                    size="small"
                                    margin="dense"
                                    sx={pdControlSx}
                                  />
                                  <div className="flex items-center mt-2 gap-2">
                                    <label className="pd-field-label">Approved</label>
                                    <FormControl size="small" variant="outlined" sx={pdControlSx}>
                                      <Select
                                        value={b.approved ? "YES" : "NO"}
                                        onChange={(e) => {
                                          const blocks = [...permitTypeBlocksState];
                                          blocks[idx].approved =
                                            e.target.value === "YES";
                                          setPermitTypeBlocksState(blocks);
                                        }}
                                      >
                                        <MenuItem value="NO">No</MenuItem>
                                        <MenuItem value="YES">Yes</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </div>
                                </div>
                              ) : (
                                <div className="mt-2" style={{ fontSize: "12px", color: "var(--pd-muted-2)" }}>
                                  Clearances pending — reviewer remark unavailable
                                </div>
                              ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p style={{ fontSize: "12.5px", color: "var(--pd-muted-2)", fontStyle: "italic" }}>No permit types added</p>
                    )}
                  </div>
                </div>

                {/* Show permit-level clearance departments in review */}
                {(permitClearanceDepartments || []).length > 0 && (
                  <div className="pd-card">
                    <div className="pd-card-head"><h3>Clearance from Departments</h3></div>
                    <div className="pd-card-body">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {(permitClearanceDepartments || []).map((cd, ci) => (
                          <div
                            key={ci}
                            style={{ fontSize: "12.5px", fontWeight: 600, color: "var(--pd-ink)", background: "var(--pd-surface-2)", border: "1px solid var(--pd-line)", borderRadius: "6px", padding: "6px 10px" }}
                          >
                            {cd.departmentName || cd.departmentId}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="pd-card">
                  <div className="pd-card-head">
                    <h3>Risk Assessment</h3>
                    <span className="pd-pill pd-pill-warn">
                      {riskMatrices.find(
                        (rm) =>
                          String(rm.id) ===
                          String(riskAssessmentData.overallRisk),
                      )?.riskLevel || "-"}
                    </span>
                  </div>
                  <div className="pd-card-body" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {riskAssessmentData.hazards.map((item, idx) => (
                      <div
                        key={idx}
                        className="pd-card"
                        style={{ borderLeft: "3px solid var(--pd-info)", background: "var(--pd-surface-2)" }}
                      >
                        <div className="pd-card-body">
                          <p style={{ fontSize: "13px", fontWeight: 700, color: "var(--pd-ink)", marginBottom: "4px" }}>
                            Step {idx + 1}: {item.taskDescription || "-"}
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-1" style={{ fontSize: "11.5px", color: "var(--pd-muted)" }}>
                            <p>
                              <span style={{ fontWeight: 600, color: "var(--pd-muted-2)" }}>Hazard: </span>
                              {hazards.find((h) => String(h.id) === item.hazardId)
                                ?.name || "-"}
                            </p>
                            <p className="sm:col-span-2">
                              <span style={{ fontWeight: 600, color: "var(--pd-muted-2)" }}>Controls: </span>
                              {[
                                ...getEffectiveControlIds(item)
                                  .map(
                                    (cid) =>
                                      controlMeasures.find(
                                        (cm) => String(cm.id) === String(cid),
                                      )?.name,
                                  )
                                  .filter(Boolean),
                                ...(item.otherControlText ? [item.otherControlText] : []),
                              ].join(", ") || "-"}
                            </p>
                            <p>
                              <span style={{ fontWeight: 600, color: "var(--pd-muted-2)" }}>Responsible: </span>
                              {item.responsiblePersonName || "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pd-card">
                  <div className="pd-card-head"><h3>Attachments</h3></div>
                  <div className="pd-card-body pd-kv-grid">
                    <Field label="Files" value={String(attachments.length)} />
                    <Field label="Existing Files" value={String(existingAttachments.length)} />
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Navigation Buttons - Fixed at bottom */}
        <div className="flex justify-between px-2 py-1 sticky bottom-0">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrev}
              className="pd-btn-add"
              style={{ background: "var(--pd-muted)", width: "auto", padding: "0 16px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600 }}
            >
              Previous
            </button>
          )}
          {currentStep < 4 ? (
            <button
              type="button"
              onClick={handleNext}
              className="pd-btn-add"
              style={{ background: "var(--pd-accent)", width: "auto", padding: "0 16px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600, marginLeft: "auto", gap: "6px" }}
            >
              Next
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          ) : (!isEditing || permitStatus === "DRAFT") ? (
            <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={() => handleSave(false)}
                disabled={loading}
                className="pd-btn-add"
                style={{ background: "var(--pd-muted)", width: "auto", padding: "0 16px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600, opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? "Saving..." : "Save as Draft"}
              </button>
              <button
                type="button"
                onClick={() => handleSave(true)}
                disabled={loading}
                className="pd-btn-add"
                style={{ background: "var(--pd-ok)", width: "auto", padding: "0 16px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600, opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? "Saving..." : "Save & Submit"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => handleSave(false)}
              disabled={loading}
              className="pd-btn-add"
              style={{ background: "var(--pd-ok)", width: "auto", padding: "0 16px", borderRadius: "6px", fontSize: "12.5px", fontWeight: 600, marginLeft: "auto", opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }}
            >
              {loading ? "Saving..." : "Save Permit"}
            </button>
          )}
        </div>
      </div >
    </div >
  );
};

export default PermitWizard;
